import i18n from '@/lib/i18n';
import { io, Socket } from 'socket.io-client';
import { tokenStorage } from './api';
import { queryClient, invalidateQueries } from './queryClient';
import { toast } from 'react-hot-toast';

// Socket.IO client configuration
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5001';

/**
 * Mirrors nova-cafe-backend/src/services/websocketService.ts -> WebSocketEvents.
 * Keep in sync; a name that exists only here is a listener that never fires.
 *
 * Previously this file declared order_updated, order_ready,
 * kitchen_order_preparing, table_order_assigned, notification_new,
 * notification_staff, user_connected, user_disconnected and
 * system_maintenance — none of which the backend emits — while omitting
 * new_order_notification, which is what the kitchen actually receives.
 */
export interface SocketEvents {
  // Order events
  'order_created': (data: any) => void;
  'order_status_changed': (data: { orderId: string; orderNumber: string; previousStatus: string; newStatus: string; tableId?: any; waiterId?: any }) => void;
  'order_item_status_changed': (data: any) => void;
  'order_cancelled': (data: any) => void;
  'new_order_notification': (data: any) => void;

  // Kitchen events
  'kitchen_order_ready': (data: any) => void;
  'kitchen_queue_updated': (data: any) => void;
  'preparation_time_update': (data: any) => void;

  // Table events
  'table_status_changed': (data: { tableId: string; tableNumber?: number; previousStatus: string; newStatus: string }) => void;
  'table_assigned': (data: any) => void;
  'table_reservation_created': (data: any) => void;

  // User events
  'waiter_assigned': (data: any) => void;
  'staff_status_changed': (data: any) => void;

  // System events
  'system_notification': (data: any) => void;
  'restaurant_stats_updated': (data: any) => void;

  // Room acknowledgements
  'joined_restaurant': (data: { restaurantId: string }) => void;
  'left_restaurant': (data: { restaurantId: string }) => void;
}

class SocketManager {
  private socket: Socket | null = null;
  private restaurantId: string | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private eventListeners: Map<string, Set<Function>> = new Map();

  constructor() {
    this.setupAuthListener();
  }

  // Setup listener for authentication changes
  private setupAuthListener() {
    window.addEventListener('auth:failure', () => {
      this.disconnect();
    });
  }

  // Connect to Socket.IO server
  connect(userId?: string, restaurantId?: string): void {
    if (restaurantId) this.restaurantId = restaurantId;
    // Skip socket connection for super admin users or if socket URL not configured
    const authState = JSON.parse(localStorage.getItem('nova-auth') || '{}');
    const user = authState?.state?.user || {};
    if (user.role === 'super_admin') {
      console.log('🔌 Skipping socket connection for super admin user');
      return;
    }

    // Guard on "already have a socket", not just "already connected": connect()
    // is now called from several places (login, rehydrate, session restore) and
    // checking `connected` alone let a second call land mid-handshake, build a
    // rival socket with forceNew, and orphan the first — which is why the room
    // was being joined twice.
    if (this.socket && (this.socket.connected || this.socket.active)) {
      // A later call may be the first one to know the restaurant, so make sure
      // we are in the room regardless.
      if (this.socket.connected && this.restaurantId) {
        this.socket.emit('join_restaurant', this.restaurantId);
      }
      return;
    }

    const token = tokenStorage.getAccessToken();
    if (!token) {
      console.warn('No access token available for socket connection');
      return;
    }

    this.socket = io(SOCKET_URL, {
      auth: {
        token,
        userId,
      },
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true,
    });

    this.setupEventHandlers();
  }

  /**
   * Re-attach every listener registered through on() to the current socket.
   *
   * on() stores callbacks in eventListeners and attaches them to whatever
   * socket exists at that moment — but connect() builds a NEW socket (forceNew),
   * so without this replay a subscriber got nothing if it subscribed before the
   * connection was up, and lost its subscription on every reconnect. The
   * kitchen display, which subscribes on mount and must survive a dropped
   * connection mid-service, would simply stop receiving orders.
   */
  private attachRegisteredListeners(): void {
    if (!this.socket) return;

    for (const [event, callbacks] of this.eventListeners.entries()) {
      for (const callback of callbacks) {
        this.socket.off(event, callback as any); // never double-register
        this.socket.on(event, callback as any);
      }
    }
  }

  // Setup Socket.IO event handlers
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.attachRegisteredListeners();

    // Connection events
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      console.log('🔌 Socket connected:', this.socket?.id);
      
      // Room-scoped broadcasts (order_created, order_status_changed,
      // table_status_changed, kitchen_queue_updated, system_notification,
      // restaurant_stats_updated) only arrive once we are in the room.
      if (this.restaurantId) {
        this.socket?.emit('join_restaurant', this.restaurantId);
      }

      // Show connection status
      if (import.meta.env.DEV) {
        toast.success(i18n.t('socket.connected'), { duration: 2000 });
      }
    });

    this.socket.on('joined_restaurant', ({ restaurantId }) => {
      console.log('🏠 Joined restaurant room:', restaurantId);
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      console.log('🔌 Socket disconnected:', reason);
      
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        this.attemptReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      // Auth rejection arrives here, not as an 'unauthorized' event — the
      // server fails the connect middleware with next(new Error(...)).
      const message = String((error as Error)?.message ?? '');
      const isAuthFailure = /auth|token|unauthor|jwt/i.test(message);

      if (isAuthFailure) {
        console.error('🔐 Socket authentication rejected:', message);
        this.disconnect();
        tokenStorage.clearTokens();
        window.dispatchEvent(new Event('auth:failure'));
        return;
      }

      console.error('🔌 Socket connection error:', message);
      this.attemptReconnect();
    });

    // Business logic events
    this.setupBusinessEventHandlers();
  }

  // Setup business logic event handlers
  private setupBusinessEventHandlers(): void {
    if (!this.socket) return;

    // Order events
    this.socket.on('order_created', (order) => {
      console.log('📝 New order created:', order);
      invalidateQueries.orders.lists();
      invalidateQueries.orders.kitchen();
      toast.success(`New order #${order.orderNumber}`, { duration: 4000 });
    });

    this.socket.on('order_status_changed', (data) => {
      console.log('📝 Order status changed:', data);
      invalidateQueries.orders.lists();
      invalidateQueries.orders.kitchen();
      invalidateQueries.orders.detail(data.orderId);
      queryClient.setQueryData(['orders', 'detail', data.orderId], data.order);
    });

    this.socket.on('order_cancelled', (order) => {
      console.log('❌ Order cancelled:', order);
      invalidateQueries.orders.all();
      invalidateQueries.tables.all();
    });

    // Table events
    this.socket.on('table_status_changed', (data) => {
      console.log('🪑 Table status changed:', data);
      invalidateQueries.tables.lists();
      invalidateQueries.tables.detail(data.tableId);
      queryClient.setQueryData(['tables', 'detail', data.tableId], data.table);
    });

    this.socket.on('table_assigned', (data) => {
      console.log('🪑 Table assigned:', data);
      invalidateQueries.tables.lists();
      invalidateQueries.tables.detail(data.tableId);
    });

    // Kitchen events
    this.socket.on('kitchen_order_ready', (order) => {
      console.log('🍳 Order ready:', order);
      invalidateQueries.orders.kitchen();
      toast.success(`Order #${order.orderNumber} is ready!`, {
        duration: 6000,
        icon: '🍽️',
      });
    });

    // Kitchen's inbound signal for a new order (role-filtered server-side to
    // chef/manager/admin). This is the hop the kitchen display depends on.
    this.socket.on('new_order_notification', (data) => {
      console.log('🍳 New order for kitchen:', data);
      invalidateQueries.orders.kitchen();
      invalidateQueries.orders.lists();
      toast.success(data.message ?? `Order ${data.orderNumber}`, { duration: 6000, icon: '🍳' });
    });

    this.socket.on('kitchen_queue_updated', (data) => {
      console.log('🍳 Kitchen queue updated:', data);
      invalidateQueries.orders.kitchen();
    });

    this.socket.on('order_item_status_changed', () => {
      invalidateQueries.orders.kitchen();
      invalidateQueries.orders.lists();
    });

    this.socket.on('system_notification', (notification) => {
      console.log('🔔 System notification:', notification);
      const toastType = notification.type === 'error' || notification.type === 'warning' ? 'error' : 'success';
      toast[toastType](notification.message, { duration: 5000 });
    });

    this.socket.on('restaurant_stats_updated', () => {
      invalidateQueries.orders.lists();
    });
  }

  // Attempt to reconnect
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('🔌 Max reconnection attempts reached');
      toast.error(i18n.t('socket.lost'), { duration: 10000 });
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.pow(2, this.reconnectAttempts) * 1000; // Exponential backoff
    
    console.log(`🔌 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
    
    setTimeout(() => {
      const token = tokenStorage.getAccessToken();
      if (token && this.socket) {
        this.socket.auth = { token };
        this.socket.connect();
      }
    }, delay);
  }

  // Disconnect from server
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.reconnectAttempts = 0;
    console.log('🔌 Socket disconnected manually');
  }

  // Emit event to server
  emit<K extends keyof SocketEvents>(event: K, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`🔌 Socket not connected, cannot emit event: ${event}`);
    }
  }

  // Subscribe to custom events
  on<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    
    this.eventListeners.get(event)!.add(callback);
    if (this.socket) {
      this.socket.on(event, callback as any);
    }

    // Return unsubscribe function
    return () => {
      this.eventListeners.get(event)?.delete(callback);
      if (this.socket) {
        this.socket.off(event, callback as any);
      }
    };
  }

  // Remove event listener
  off<K extends keyof SocketEvents>(event: K, callback?: SocketEvents[K]): void {
    if (callback) {
      this.eventListeners.get(event)?.delete(callback);
      if (this.socket) {
        this.socket.off(event, callback as any);
      }
    } else {
      this.eventListeners.delete(event);
      if (this.socket) {
        this.socket.off(event);
      }
    }
  }

  // Get connection status
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  // Get socket ID
  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  /** Backend listens for 'join_restaurant' with a bare restaurantId string. */
  joinRestaurant(restaurantId: string): void {
    this.restaurantId = restaurantId;
    this.socket?.emit('join_restaurant', restaurantId);
  }

  leaveRestaurant(restaurantId: string): void {
    this.socket?.emit('leave_restaurant', restaurantId);
    if (this.restaurantId === restaurantId) this.restaurantId = null;
  }
}

// Create and export socket manager instance
export const socketManager = new SocketManager();

// Convenience functions
export const connectSocket = (userId?: string, restaurantId?: string) =>
  socketManager.connect(userId, restaurantId);
export const disconnectSocket = () => socketManager.disconnect();
export const emitSocketEvent = (event: keyof SocketEvents, data?: any) => socketManager.emit(event, data);
export const onSocketEvent = <K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]) => 
  socketManager.on(event, callback);
export const offSocketEvent = <K extends keyof SocketEvents>(event: K, callback?: SocketEvents[K]) => 
  socketManager.off(event, callback);
export const isSocketConnected = () => socketManager.isSocketConnected();

// Hook for using socket in React components
export const useSocket = () => {
  return {
    socket: socketManager,
    isConnected: socketManager.isSocketConnected(),
    emit: socketManager.emit.bind(socketManager),
    on: socketManager.on.bind(socketManager),
    off: socketManager.off.bind(socketManager),
    connect: socketManager.connect.bind(socketManager),
    disconnect: socketManager.disconnect.bind(socketManager),
  };
};